import webApiClient from '@tunein/web-api-client-legacy';
import { pick } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';
import { apiBeta } from 'src/client/api';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { FETCH_CONFIG } from 'src/common/actions/config';
import { FETCH_ME } from 'src/common/actions/me';
import {
  CLIENT_API_RETRY_LIMIT,
  CLIENT_API_TIMEOUT,
} from 'src/common/constants/experiments/config';
import {
  UNIFIED_EVENTS_BATCH_INTERVAL_SECS,
  UNIFIED_EVENTS_MAX_BATCH_COUNT,
} from 'src/common/constants/experiments/reporting';
import { config as partnersConfig } from 'src/common/constants/partners/alexa';
import { fulfilled } from 'src/common/reducers/utils/asyncActionNameSuffixes';
import { selectUserIpAddress } from 'src/common/selectors/app';
import { selectIsUserSubscribed } from 'src/common/selectors/me';
import getConfigStreamFormats from 'src/common/utils/getConfigStreamFormats';
import { configApiOptions } from '../../common/config/configApiOptions';
import { getBaseApiConfig } from '../../common/config/utils';
import { FORCE_HTTPS_STREAMS } from '../../common/constants/experiments/player';
import { getGuildIdQueryParam } from '../../common/utils/discord';
import { isDevBuild } from '../../common/utils/environment';

export default function createTuneInApiClient(initialState = {}) {
  const { auth, config, app = {}, me } = initialState;
  const rawAuthTokens = pick(auth?.tokenInfo || {}, ['accessToken', 'refreshToken']);
  const authTokens = isEmpty(rawAuthTokens) ? null : rawAuthTokens;

  const rmConfigs = config?.autoupdate || {};
  const { isMobile, isDesktop, isDiscord, desktopVersion, tuneInUserSerial, partnerId, userAgent } =
    app;

  const apiConfig = {
    ...getBaseApiConfig(isDiscord),
    configApiOptions,
    partnerId,
    serial: tuneInUserSerial,
    streamFormats: getConfigStreamFormats(isMobile, isDesktop, isDiscord, userAgent),
    ...(desktopVersion && { desktopVersion }),
    ...(isDiscord && { productMode: getGuildIdQueryParam() }),
    graphQlTransformAllowlist: { getUser: true },

    // Config-based options -- new API client accesses these internally
    timeout: rmConfigs[CLIENT_API_TIMEOUT],
    retryLimit: rmConfigs[CLIENT_API_RETRY_LIMIT],
    reportingIntervalDelaySecs: rmConfigs[UNIFIED_EVENTS_BATCH_INTERVAL_SECS],
    reportingMaxBatch: rmConfigs[UNIFIED_EVENTS_MAX_BATCH_COUNT],
    forceHttpsStreams: rmConfigs[FORCE_HTTPS_STREAMS],

    // misc
    debug: isDevBuild(),

    // Sync legacy WAC auth tokens to new WAC
    onSetNewTokens: (_apiContext, authTokens) => {
      if (apiBeta.authTokens?.accessToken !== authTokens?.accessToken) {
        apiBeta.setAuthTokens(authTokens);
      }
    },
  };

  // TODO: disabled per TUNE-17244 - revisiting this when it won't cause our URLs to be translated
  // const requestHeaders = {
  //   ...(acceptLanguageHeader && { 'Accept-Language': acceptLanguageHeader }),
  // };
  const client = webApiClient(apiConfig);

  if (authTokens) {
    client.setTokens(authTokens);
  }

  apiBeta.init({
    ...getBaseApiConfig(isDiscord),
    configServiceOptions: configApiOptions,
    partnersServiceOptions: partnersConfig,
    uiServiceOptions: {
      generateRoutePathForGuideItem: (guideItem) => {
        return guideItem.seoInfo?.cleanPath || guideItem.destinationInfo?.cleanPath || '';
      },
    },
    configData: config,
    userData: me?.details || null,
    partnerId,
    serial: tuneInUserSerial,
    authTokens,
    optionalParams: {
      formats: getConfigStreamFormats(isMobile, isDesktop, isDiscord, userAgent),
      desktopVersion,
      ...(isDiscord && { productMode: getGuildIdQueryParam() }),
    },
    debug: isDevBuild(),
    isPremiumOverride: selectIsUserSubscribed(initialState),
    ipAddress: selectUserIpAddress(initialState),
  });

  // Sync new WAC auth tokens to legacy WAC
  apiBeta.on('authTokensUpdated', (authTokens) => {
    if (authTokens?.accessToken) {
      // WAC legacy's setTokens() only emits a token event if we pass in `true` as the second argument
      client.setTokens(authTokens);
      return;
    }

    // Avoids a loop, since both WACs emit an event when tokens are updated
    if (client.getTokens()?.accessToken) {
      client.clearTokens();
    }
  });

  apiBeta.on('configData', (configData) => {
    storeSingleton.dispatch({
      type: fulfilled(FETCH_CONFIG),
      payload: { ...configData, loadedTime: Date.now() },
    });
  });

  apiBeta.on('userData', (userData) => {
    storeSingleton.dispatch({
      type: fulfilled(FETCH_ME),
      payload: {
        content: userData,
        loadedTime: Date.now(),
      },
      meta: {
        contentId: 'details',
      },
    });
  });

  return client;
}
